import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { apiHosts } from "@/api/constants/apiHosts";
import { ToastOptionsError, ToastOptionsSuccess } from "@modernary/mui/components/toasts";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import type {
  Organization,
  OrganizationElectronicForm,
  OrganizationNew,
  OrganizationPermitVersion,
  OrganizationQuery,
} from "./organization";
import organizationService from "./organizationService";
import { contactMethodItems } from "@/views/person/person";

const URL = "organizations";
const ENTITY_NAME = "Organization";

const axiosParams = {
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Organization>(apiHosts.AQMS, axiosParams);

function withDisplayName(organization: Organization): Organization {
  return {
    ...organization,
    displayName: organization.name,
    contactMethods: contactMethodItems(organization.contactMethods),
  };
}

const getOrganizations = (params: OrganizationQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api
    .getPaged(url, {
      params,
      paramsSerializer: serializeQueryParams,
      abort: config?.abortMethod,
      successToastCb,
      errorToastCb,
    })
    .then((response) => {
      response.data._items = response.data._items?.map((item) => withDisplayName(item));
      return response;
    });
};

type OrganizationIds = {
  id?: string | number;
};
const getNewOrganization = async (ids: OrganizationIds) => {
  const defaultApp = {
    data: {
      ...organizationService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<OrganizationNew, any>;

  return defaultApp;
};

const getOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api
    .get(URL + "/" + id, {
      successToastCb,
      errorToastCb,
    })
    .then((response) => {
      response.data = withDisplayName(response.data);
      return response;
    });
};

const createOrganization = (organization: OrganizationNew, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.post(URL, organization, {
    successToastCb,
    errorToastCb,
  });
};

const updateOrganization = (organization: Organization, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.put(`${URL}/${organization.id}`, organization, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {Organization} organization
 */
const saveOrganization = (organization: OrganizationNew, config = {}) => {
  if (organization.id) {
    return updateOrganization(organization as Organization, config);
  } else {
    return createOrganization(organization, config);
  }
};

const deleteOrganizationById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successToastCb,
    errorToastCb,
  });
};

const getOrganizationChildPage = <T>(
  url: string,
  fetched: string,
  params: OrganizationQuery,
  config: PagingApiAndNotifierConfig,
) =>
  api.getPaged<Paging<T>>(config.pagingUrl ?? url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config.abortMethod,
    successToastCb: (options: ToastOptionsSuccess) => {
      options.hide = true;
      config.successToastCb?.(options);
    },
    errorToastCb: (options: ToastOptionsError) => {
      options.fetched = fetched;
      config.errorToastCb?.(options);
    },
  });

const getOrganizationElectronicForms = (
  organizationId: string | number,
  params: OrganizationQuery,
  config: PagingApiAndNotifierConfig = {},
) =>
  getOrganizationChildPage<OrganizationElectronicForm>(
    `${URL}/${organizationId}/electronic-forms`,
    "Electronic Forms",
    params,
    config,
  );

const getOrganizationPermitVersions = (
  organizationId: string | number,
  params: OrganizationQuery,
  config: PagingApiAndNotifierConfig = {},
) =>
  getOrganizationChildPage<OrganizationPermitVersion>(
    `${URL}/${organizationId}/permit-versions`,
    "Permits",
    params,
    config,
  );

export {
  getOrganizations,
  getNewOrganization,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  saveOrganization,
  deleteOrganizationById,
  getOrganizationElectronicForms,
  getOrganizationPermitVersions,
};
